/**
 * Goal Score API — PR-7a (publish consent).
 *
 * Covers:
 *   - getPublishStatus: GET /evaluations/{id}/publish-status
 *   - setPublishConsent: PATCH /evaluations/{id}/publish-consent
 *
 * Kept in its own module (not added to a large shared evaluation-API file)
 * per the Goal Score delivery plan's parallel-build discipline. Same
 * apiClient / try-catch convention as `leaderboardApi.ts`.
 */
import { apiClient } from '@/services/api';
import type { PublishConsentResponse, PublishStatusResponse, ValidationErrorBody } from '@/types/goalScore';

/**
 * Current leaderboard publish state + ranking eligibility for one
 * evaluation. Read BEFORE rendering the consent control so it never
 * silently fails to appear (`docs/API_BACKEND.md` §9.8).
 */
export async function getPublishStatus(evaluationId: string): Promise<PublishStatusResponse> {
  const { data } = await apiClient.get<PublishStatusResponse>(
    `/evaluations/${evaluationId}/publish-status`,
  );
  return data;
}

/**
 * Set this one evaluation's leaderboard publish consent.
 *
 * `publish: true` -> private|hidden -> published.
 * `publish: false` -> published -> hidden (no-op from private).
 *
 * Throws the underlying axios error on 403/404/422 — callers render the
 * error body as plain copy (see `GoalScorePublishConsent.tsx`); a 422's
 * body is `{message, fields}` (the project's validation-error normalizer),
 * a 403/404's body is `{detail: string}`.
 */
export async function setPublishConsent(
  evaluationId: string,
  publish: boolean,
): Promise<PublishConsentResponse> {
  const { data } = await apiClient.patch<PublishConsentResponse>(
    `/evaluations/${evaluationId}/publish-consent`,
    { publish },
  );
  return data;
}

/** Extracts a plain-language message from a publish-consent error response, for any of the three documented shapes. */
export function extractPublishConsentErrorMessage(error: unknown): string {
  const response = (error as { response?: { data?: unknown; status?: number } } | undefined)?.response;
  const data = response?.data as Partial<ValidationErrorBody & { detail?: string }> | undefined;
  if (data?.message) return data.message;
  if (data?.detail) return data.detail;
  return 'Something went wrong updating your leaderboard setting. Please try again.';
}
